// Currency utilities for PlateProfit

export const CURRENCIES = {
  // Prefix currencies (symbol before amount)
  "USD": { symbol: "$", position: "prefix", name: "US Dollar" },
  "CAD": { symbol: "$", position: "prefix", name: "Canadian Dollar" },
  "EUR": { symbol: "€", position: "prefix", name: "Euro" },
  "GBP": { symbol: "£", position: "prefix", name: "British Pound" },
  "INR": { symbol: "₹", position: "prefix", name: "Indian Rupee" },
  "JPY": { symbol: "¥", position: "prefix", name: "Japanese Yen" },
  "CNY": { symbol: "¥", position: "prefix", name: "Chinese Yuan" },
  "BRL": { symbol: "R$", position: "prefix", name: "Brazilian Real" },
  "ZAR": { symbol: "R", position: "prefix", name: "South African Rand" },
  "THB": { symbol: "฿", position: "prefix", name: "Thai Baht" },
  "PHP": { symbol: "₱", position: "prefix", name: "Philippine Peso" },
  "MYR": { symbol: "RM", position: "prefix", name: "Malaysian Ringgit" },
  "IDR": { symbol: "Rp", position: "prefix", name: "Indonesian Rupiah" },
  "KRW": { symbol: "₩", position: "prefix", name: "South Korean Won" },
  "TRY": { symbol: "₺", position: "prefix", name: "Turkish Lira" },
  "RUB": { symbol: "₽", position: "prefix", name: "Russian Ruble" },
  "CHF": { symbol: "CHF", position: "prefix", name: "Swiss Franc" },
  "AUD": { symbol: "$", position: "prefix", name: "Australian Dollar" },
  "SGD": { symbol: "$", position: "prefix", name: "Singapore Dollar" },
  "HKD": { symbol: "$", position: "prefix", name: "Hong Kong Dollar" },
  "MXN": { symbol: "$", position: "prefix", name: "Mexican Peso" },
  
  // Suffix currencies (symbol after amount)
  "QAR": { symbol: "QR", position: "suffix", name: "Qatari Riyal" },
  "AED": { symbol: "AED", position: "suffix", name: "UAE Dirham" },
  "SAR": { symbol: "SAR", position: "suffix", name: "Saudi Riyal" },
  "KWD": { symbol: "KD", position: "suffix", name: "Kuwaiti Dinar" },
  "BHD": { symbol: "BD", position: "suffix", name: "Bahraini Dinar" },
  "OMR": { symbol: "OMR", position: "suffix", name: "Omani Rial" },
  "PLN": { symbol: "zł", position: "suffix", name: "Polish Zloty" },
  "SEK": { symbol: "kr", position: "suffix", name: "Swedish Krona" },
  "NOK": { symbol: "kr", position: "suffix", name: "Norwegian Krone" },
  "DKK": { symbol: "kr", position: "suffix", name: "Danish Krone" },
};

/**
 * Get locale based on language
 * @param {string} language - Language code (e.g., "en", "es", "fr", "ar")
 * @returns {string} Locale string for Intl API
 */
export const getLocaleFromLanguage = (language = "en") => {
  const localeMap = {
    en: "en-US",
    es: "es-ES",
    fr: "fr-FR",
    ar: "ar-SA"
  };
  return localeMap[language] || "en-US";
};

/**
 * Format a number as currency with proper symbol, precision, and locale-aware separators
 * @param {string|number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., "USD", "EUR")
 * @param {number} decimals - Number of decimal places (default: 2, use 6 for unit costs)
 * @param {string} language - Language code for locale formatting
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = "USD", decimals = 2, language = "en") => {
  const currencyInfo = CURRENCIES[currency] || CURRENCIES["USD"];
  
  // Convert to number
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  
  // Get locale for proper number formatting
  const locale = getLocaleFromLanguage(language);
  
  // Use Intl.NumberFormat for locale-aware decimal and thousands separators
  const formattedAmount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true
  }).format(numAmount);
  
  // Return with symbol in correct position
  if (currencyInfo.position === "prefix") {
    return `${currencyInfo.symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${currencyInfo.symbol}`;
  }
};

/**
 * Format date based on locale
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code
 * @returns {string} Formatted date string
 */
export const formatDate = (date, language = "en") => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const locale = getLocaleFromLanguage(language);
  
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric"
  }).format(dateObj);
};

/**
 * Format date and time based on locale
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, language = "en") => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const locale = getLocaleFromLanguage(language);
  
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(dateObj);
};

/**
 * Calculate unit cost with 6-decimal precision
 * @param {string|number} bulkPrice - Bulk price
 * @param {string|number} bulkQuantity - Bulk quantity
 * @returns {string} Unit cost as string with 6 decimals
 */
export const calculateUnitCost = (bulkPrice, bulkQuantity) => {
  try {
    const price = typeof bulkPrice === "string" ? parseFloat(bulkPrice) : bulkPrice;
    const quantity = typeof bulkQuantity === "string" ? parseFloat(bulkQuantity) : bulkQuantity;
    
    if (isNaN(price) || isNaN(quantity) || quantity === 0) {
      return "0.000000";
    }
    
    const unitCost = price / quantity;
    return unitCost.toFixed(6);
  } catch (error) {
    return "0.000000";
  }
};
